import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import employeeService from '../../../services/employeeService';

const AddEmployeeModal = ({ onClose, onSubmit, companyId }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    employeeNumber: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    employmentType: 'full_time',
    citizenStatus: 'citizen',
    department: '',
    position: '',
    hireDate: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [limitValidation, setLimitValidation] = useState(null);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    // Validation
    if (!formData?.employeeNumber || !formData?.firstName || !formData?.lastName || !formData?.email) {
      setError('Please fill in all required fields');
      return;
    }

    if (!formData?.department || !formData?.position || !formData?.hireDate) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);

      // Validate employee limit before adding
      if (companyId) {
        const validation = await employeeService?.validateEmployeeLimit(companyId, 1);
        
        if (!validation?.allowed) {
          setLimitValidation(validation);
          setShowUpgradePrompt(true);
          setLoading(false);
          return;
        }
      }

      await onSubmit?.(formData);
    } catch (err) {
      setError(err?.message || 'Failed to add employee');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgradeClick = () => {
    onClose?.();
    navigate('/billing-subscription-management');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-bold text-foreground">Add New Employee</h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="bg-error/10 border border-error text-error px-4 py-3 rounded-md mb-4">
              <p className="text-sm">{error}</p>
            </div>
          )}

          {showUpgradePrompt && limitValidation && (
            <div className="bg-warning/10 border border-warning rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3 mb-4">
                <Icon name="AlertTriangle" size={20} className="text-warning mt-0.5" />
                <div className="flex-1">
                  <p className="font-semibold text-warning mb-1">Employee Limit Reached</p>
                  <p className="text-sm text-warning/90">{limitValidation?.error}</p>
                </div>
              </div>

              <div className="bg-background rounded-lg p-3 mb-3">
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-muted-foreground">Current Plan:</span>
                  <span className="font-medium text-foreground">
                    {limitValidation?.currentTier?.replace(/_/g, ' ')?.toUpperCase()}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Employees:</span>
                  <span className="font-medium text-foreground">
                    {limitValidation?.currentCount} / {limitValidation?.maxAllowed}
                  </span>
                </div>
              </div>

              {limitValidation?.nextTier && (
                <div className="bg-primary/10 rounded-lg p-3 mb-3 border border-primary">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="TrendingUp" size={14} className="text-primary" />
                    <span className="text-xs font-semibold text-primary">Upgrade to {limitValidation?.nextTier?.name?.replace(/_/g, ' ')?.toUpperCase()}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Up to {limitValidation?.nextTier?.maxEmployees} employees</span>
                    <span className="font-medium text-foreground">K{limitValidation?.nextTier?.price?.toLocaleString()}/month</span>
                  </div>
                </div>
              )}

              <Button
                type="button"
                variant="default"
                size="sm"
                onClick={handleUpgradeClick}
                className="w-full"
              >
                <Icon name="CreditCard" size={14} className="mr-2" />
                Upgrade Plan Now
              </Button>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Employee Number <span className="text-error">*</span>
              </label>
              <Input
                value={formData?.employeeNumber}
                onChange={(e) => handleChange('employeeNumber', e?.target?.value)}
                placeholder="EMP001"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Email <span className="text-error">*</span>
              </label>
              <Input
                type="email"
                value={formData?.email}
                onChange={(e) => handleChange('email', e?.target?.value)}
                placeholder="employee@company.com"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                First Name <span className="text-error">*</span>
              </label>
              <Input
                value={formData?.firstName}
                onChange={(e) => handleChange('firstName', e?.target?.value)}
                placeholder="John"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Last Name <span className="text-error">*</span>
              </label>
              <Input
                value={formData?.lastName}
                onChange={(e) => handleChange('lastName', e?.target?.value)}
                placeholder="Doe"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Phone
              </label>
              <Input
                type="tel"
                value={formData?.phone}
                onChange={(e) => handleChange('phone', e?.target?.value)}
                placeholder="+675 1234 5678"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Date of Birth
              </label>
              <Input
                type="date"
                value={formData?.dateOfBirth}
                onChange={(e) => handleChange('dateOfBirth', e?.target?.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Department <span className="text-error">*</span>
              </label>
              <Input
                value={formData?.department}
                onChange={(e) => handleChange('department', e?.target?.value)}
                placeholder="Human Resources"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Position <span className="text-error">*</span>
              </label>
              <Input
                value={formData?.position}
                onChange={(e) => handleChange('position', e?.target?.value)}
                placeholder="HR Manager"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Employment Type <span className="text-error">*</span>
              </label>
              <Select
                value={formData?.employmentType}
                onChange={(e) => handleChange('employmentType', e?.target?.value)}
                required
              >
                <option value="full_time">Full-time</option>
                <option value="part_time">Part-time</option>
                <option value="contract">Contract</option>
                <option value="casual">Casual</option>
                <option value="temporary">Temporary</option>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Citizenship Status <span className="text-error">*</span>
              </label>
              <Select
                value={formData?.citizenStatus}
                onChange={(e) => handleChange('citizenStatus', e?.target?.value)}
                required
              >
                <option value="citizen">PNG Citizen</option>
                <option value="permanent_resident">Permanent Resident</option>
                <option value="work_permit">Work Permit Holder</option>
                <option value="temporary_resident">Temporary Resident</option>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Hire Date <span className="text-error">*</span>
              </label>
              <Input
                type="date"
                value={formData?.hireDate}
                onChange={(e) => handleChange('hireDate', e?.target?.value)}
                required
              />
            </div>
          </div>

          <div className="flex gap-3 justify-end mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Icon name="Loader2" size={16} className="mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Icon name="Plus" size={16} className="mr-2" />
                  Add Employee
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEmployeeModal;
